/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useState, useMemo, useEffect } from 'react';

/**
 * @typedef {object} PanelConfig
 * @property {boolean} initialCollapsed
 * @property {string} expandedHeight
 */

/**
 * @typedef {object} PanelProps
 * @property {boolean} isCollapsed
 * @property {() => void} onToggleCollapsed
 * @property {string|undefined} expandedHeight
 */

/**
 * Manages the collapsed state and heights of a group of collapsible panels.
 * When only one panel is expanded, it takes up all available space.
 * @param {Array<PanelConfig>} configs - Configuration for each panel in the group.
 * @returns {Array<PanelProps>} An array of props to be passed to each panel component.
 */
export const useCollapsibleGroup = (configs) => {
  const [collapsedStates, setCollapsedStates] = useState(() => configs.map((c) => c.initialCollapsed));

  useEffect(() => {
    setCollapsedStates(configs.map((c) => c.initialCollapsed));
  }, [configs]);

  const toggleFns = useMemo(
    () =>
      configs.map((_, index) => () => {
        setCollapsedStates((prev) => {
          const newStates = [...prev];
          newStates[index] = !newStates[index];

          // Always keep one panel expanded
          if (newStates.every((state) => state)) {
            const nextIndex = (index + 1) % configs.length;
            newStates[nextIndex] = false;
          }

          return newStates;
        });
      }),
    [configs]
  );

  const panelProps = useMemo(() => {
    return configs.map((config, index) => {
      return {
        isCollapsed: collapsedStates[index],
        onToggleCollapsed: toggleFns[index],
        expandedHeight: config.expandedHeight
      };
    });
  }, [configs, collapsedStates, toggleFns]);

  // Do not return collapse toggle func if only one panel is available (i.e., panel shouldn't be collapsible)
  if (panelProps.length === 1) {
    panelProps[0].onToggleCollapsed = null;
  }

  return panelProps;
};
